/**
 * Hover intent for Horizon's top-level menu — add-on script (new file; no
 * Horizon file is edited).
 *
 * Problem: the dropdown panel sits at the left of the page, so for a nav item
 * further right (e.g. "Learn") the pointer has a long diagonal journey to it.
 * Along the way it leaves the item, may pass over several neighbouring items
 * and cross open space. Horizon reacts to every one of those boundaries
 * instantly (closing, or switching to a neighbour), so the dropdown vanishes
 * before the pointer arrives.
 *
 * Fix: this sits in front of Horizon's own pointerenter / pointerleave
 * handling (it listens on `document` in the capture phase, so a `window`
 * capture listener runs first). While a dropdown is open and the pointer
 * leaves its item, the leave (and any hover on a neighbouring item) is held
 * back for as long as the pointer keeps getting closer to the open panel.
 *   - Reaches the panel: Horizon never saw a leave, nothing changes.
 *   - Stops moving toward it (or turns away): the held events are replayed,
 *     so Horizon closes or switches exactly as it would have, just a moment
 *     later. Settling on a neighbouring item therefore still switches to it.
 * Leaving the menu in a direction that isn't toward the panel is never delayed.
 */
(() => {
  'use strict';

  if (window.__nmHoverIntent) return;
  window.__nmHoverIntent = true;

  /** How long the pointer may go without getting closer to the panel before the hold ends. */
  const NO_PROGRESS_MS = 220;
  /** Absolute ceiling for one hold, however slowly the pointer travels. */
  const HOLD_MAX_MS = 1500;
  /** Getting this much further from the panel than the closest point so far counts as turning away. */
  const RETREAT_PX = 28;
  /** Distance gained toward the panel that counts as progress. */
  const PROGRESS_PX = 2;

  /** Events we replay ourselves, so the listeners below let them through. */
  const replayed = new WeakSet();

  /**
   * @typedef {Object} Hold
   * @property {HTMLElement} li - the open top-level item whose leave is being held
   * @property {Node | null} related - where the pointer went when it left
   * @property {number} best - closest the pointer has been to the panel so far
   * @property {number} lastProgress - timestamp of the last time it got closer
   * @property {number} startedAt
   */

  /** @type {Hold | null} */
  let hold = null;
  /** @type {HTMLElement | null} */
  let candidate = null;
  let timer;

  /** Last pointer position seen by a pointermove (a leave event fires before the move that caused it). */
  let pointer = { x: 0, y: 0 };

  const isTopLevelItem = (el) => el instanceof HTMLElement && el.classList.contains('menu-list__list-item') && el.slot === '';

  /** The default-slot item whose own submenu Horizon currently has open. */
  function activeItemIn(menu) {
    const items = menu.querySelectorAll('.menu-list__list-item');
    for (const li of items) {
      if (li.slot === '' && li.querySelector(':scope > .menu-list__submenu[data-active]')) return li;
    }
    return null;
  }

  /** The visible dropdown area the pointer is trying to reach. */
  function panelRect(li) {
    const submenu = li.querySelector(':scope > .menu-list__submenu');
    const target = submenu?.querySelector('.nm-panel') ?? submenu?.querySelector('.menu-list__submenu-inner') ?? submenu;
    return target ? target.getBoundingClientRect() : null;
  }

  function distanceTo(rect, x, y) {
    const dx = Math.max(rect.left - x, 0, x - rect.right);
    const dy = Math.max(rect.top - y, 0, y - rect.bottom);
    return Math.hypot(dx, dy);
  }

  function reset() {
    clearTimeout(timer);
    timer = undefined;
    hold = null;
    candidate = null;
  }

  function armTimer() {
    clearTimeout(timer);
    if (!hold) return;
    const now = performance.now();
    const wait = Math.min(NO_PROGRESS_MS - (now - hold.lastProgress), HOLD_MAX_MS - (now - hold.startedAt));
    timer = setTimeout(commit, Math.max(0, wait));
  }

  function replay(target, type, init) {
    const event = new PointerEvent(type, { bubbles: false, pointerType: 'mouse', ...init });
    replayed.add(event);
    target.dispatchEvent(event);
  }

  /** End the hold: let Horizon see what it would have seen, unless the pointer is back. */
  function commit() {
    const current = hold;
    const next = candidate;
    reset();
    if (!current || !current.li.isConnected) return;

    const menu = current.li.closest('header-menu');
    if (!menu || activeItemIn(menu) !== current.li) return; // state already moved on
    if (current.li.matches(':hover')) return; // pointer is back on the open item

    replay(current.li, 'pointerleave', { relatedTarget: current.related });
    if (next && next.isConnected && next.matches(':hover')) replay(next, 'pointerenter');
  }

  /** Start (or keep) a hold on `li`'s leave, measuring progress from the given position. */
  function startHold(li, related, x, y) {
    if (!hold) {
      const rect = panelRect(li);
      const best = rect ? distanceTo(rect, x, y) : Infinity;
      const now = performance.now();
      hold = { li, related, best, lastProgress: now, startedAt: now };
    }
    armTimer();
  }

  window.addEventListener(
    'pointermove',
    (event) => {
      pointer = { x: event.clientX, y: event.clientY };
      if (!hold) return;

      const rect = panelRect(hold.li);
      if (!rect) {
        commit();
        return;
      }
      const distance = distanceTo(rect, pointer.x, pointer.y);
      if (distance > hold.best + RETREAT_PX) {
        commit(); // turned away from the panel
        return;
      }
      if (distance < hold.best - PROGRESS_PX) {
        hold.best = distance;
        hold.lastProgress = performance.now();
        armTimer();
      }
    },
    { capture: true, passive: true }
  );

  window.addEventListener(
    'pointerenter',
    (event) => {
      if (replayed.has(event) || event.pointerType === 'touch') return;
      const li = event.target;
      if (!isTopLevelItem(li)) return;

      const menu = li.closest('header-menu');
      if (!menu) return;
      const active = activeItemIn(menu);
      if (!active) return;

      if (li === active) {
        // Back on the open item, or arrived at its panel, before anything committed.
        reset();
        return;
      }

      // Onto a different top-level item while one is open: hold it back.
      event.stopImmediatePropagation();
      candidate = li;
      startHold(active, li, pointer.x, pointer.y);
    },
    true
  );

  window.addEventListener(
    'pointerleave',
    (event) => {
      if (replayed.has(event) || event.pointerType === 'touch') return;
      const li = event.target;
      if (!isTopLevelItem(li)) return;

      const menu = li.closest('header-menu');
      if (!menu) return;
      const active = activeItemIn(menu);

      if (li !== active) {
        // Leaving a neighbour we crossed on the way. Horizon never saw it being
        // entered (we held that back), and would treat the leave as "close the
        // open menu", so it is held back too while a hold is in progress.
        if (li === candidate) candidate = null;
        if (hold) event.stopImmediatePropagation();
        return;
      }

      const related = event.relatedTarget;
      if (related instanceof Node && active.contains(related)) return; // still inside the open item

      const hasPosition = event.clientX !== 0 || event.clientY !== 0;
      const x = hasPosition ? event.clientX : pointer.x;
      const y = hasPosition ? event.clientY : pointer.y;

      // Onto a neighbour or elsewhere in the menu bar: always hold.
      const withinMenu = related instanceof Node && menu.contains(related);

      if (!withinMenu) {
        // Out of the menu bar: only hold if the pointer is heading for the panel.
        const rect = panelRect(active);
        if (!rect) return;
        const distanceNow = distanceTo(rect, x, y);
        const distanceBefore = distanceTo(rect, pointer.x, pointer.y);
        if (!(distanceNow > 0 && distanceNow <= distanceBefore + PROGRESS_PX)) return;
      }

      event.stopImmediatePropagation();
      startHold(active, related instanceof Node ? related : null, x, y);
    },
    true
  );
})();
